import { Tooltip } from '@mantine/core'

/**
 * Generic horizontal stacked bar component. Renders one bar split into N
 * proportional segments, each with its own color + tooltip.
 *
 * Domain-neutral: the consumer provides `segments` with `value`/`color`/
 * `label` and the bar handles the math. Used inside table cells (e.g.
 * `LeaderboardCardView` custom render fns) for per-row distributions.
 *
 * Empty state: when total is 0, renders a single muted gray bar so the
 * row still has visual weight (matches the height of populated rows).
 */

export interface StackedBarSegment {
  /** Stable key for React. */
  id: string
  /** Numeric weight of this segment. Negative values are clamped to 0. */
  value: number
  /** CSS color (hex / rgb / etc). */
  color: string
  /** Tooltip label shown on hover. e.g. "Hired · 12". */
  label: string
  /** Text color for the inline label (only used with `inlineLabels`). */
  textColor?: string
}

export interface StackedBarProps {
  segments: ReadonlyArray<StackedBarSegment>
  /** Bar height in pixels. Defaults to 8. */
  height?: number
  /** Min visible segment width as a fraction of the bar (so tiny segments
   *  don't disappear). Defaults to 0.02. Set 0 to disable. */
  minSegmentFraction?: number
  /** Render each segment's label inside the segment (clipped when it doesn't
   *  fit — the hover tooltip always carries the full text). */
  inlineLabels?: boolean
  /** Optional className passed to the outer wrapper. */
  className?: string
  /** Test hook id for the outer wrapper. */
  'data-testid'?: string
}

const EMPTY_BAR_COLOR = '#E5E7EB' // gray-200

export const StackedBar = ({
  segments,
  height = 8,
  minSegmentFraction = 0.02,
  inlineLabels = false,
  className,
  'data-testid': dataTestId,
}: StackedBarProps) => {
  const positiveSegments = segments
    .map((s) => ({ ...s, value: Math.max(0, s.value) }))
    .filter((s) => s.value > 0)

  const total = positiveSegments.reduce((acc, s) => acc + s.value, 0)

  // Empty state — render a single muted gray bar so the row keeps its
  // baseline height and the table doesn't shift on data-load.
  if (total === 0) {
    return (
      <div
        className={className}
        data-testid={dataTestId}
        style={{
          width: '100%',
          height,
          borderRadius: height / 2,
          background: EMPTY_BAR_COLOR,
          opacity: 0.6,
        }}
      />
    )
  }

  return (
    <div
      className={className}
      data-testid={dataTestId}
      style={{
        display: 'flex',
        width: '100%',
        height,
        borderRadius: height / 2,
        overflow: 'hidden',
        // Subtle tint behind segments — visible only if rounding/min-width
        // leaves a sliver gap.
        background: EMPTY_BAR_COLOR,
      }}
    >
      {positiveSegments.map((segment) => {
        const rawFraction = segment.value / total
        // Lift tiny but non-zero segments to a minimum visible width so
        // they don't render as a 0px sliver. The other segments compress
        // proportionally; given a typical 5-stage funnel this rounding
        // error stays imperceptible.
        const fraction = Math.max(rawFraction, minSegmentFraction)
        return (
          <Tooltip key={segment.id} label={segment.label} withArrow>
            <div
              data-testid={`stacked-bar-segment-${segment.id}`}
              style={{
                width: `${fraction * 100}%`,
                background: segment.color,
                height: '100%',
                cursor: 'help',
                ...(inlineLabels && {
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }),
              }}
            >
              {inlineLabels && (
                <span
                  className="px-2 text-[11px] font-medium"
                  style={{ color: segment.textColor }}
                >
                  {segment.label}
                </span>
              )}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}
